// utils/FormatUtils.ts

export class FormatUtils {
  static formatFaxNumber(input: any): string {
    if (input == null) {
      return "";
    }

    // Allow '+' at the start and remove all other non-digit characters
    let numericInputFormatted = input.replace(/[^0-9+]/g, "");

    // Format based on length
    switch (numericInputFormatted.length) {
      case 7:
        return numericInputFormatted.replace(/(\d{3})(\d{4})/, "$1-$2"); // Local: XXX-XXXX
      case 10:
        return numericInputFormatted.replace(
          /(\d{3})(\d{3})(\d{4})/,
          "$1-$2-$3"
        ); // National: XXX-XXX-XXXX
      case 11:
        if (numericInputFormatted[0] === "+") {
          return numericInputFormatted.replace(
            /(\+)(\d)(\d{3})(\d{3})(\d{4})/,
            "$1$2-$3-$4-$5"
          ); // International: +X-XXX-XXX-XXXX
        }
        return numericInputFormatted.replace(
          /(\d)(\d{3})(\d{3})(\d{4})/,
          "$1-$2-$3-$4"
        );
      case 12: // Handle cases like +12031234567
        if (numericInputFormatted[0] === "+") {
          return numericInputFormatted.replace(
            /(\+)(\d)(\d{3})(\d{3})(\d{4})/,
            "$1$2-$3-$4-$5"
          ); // International: +X-XXX-XXX-XXXX
        }
        return numericInputFormatted.replace(
          /(\d)(\d{3})(\d{3})(\d{4})/,
          "$1$2-$3-$4"
        );
      default:
        if (numericInputFormatted.length === 10) {
          return numericInputFormatted.replace(
            /(\d{3})(\d{3})(\d{4})/,
            "$1-$2-$3"
          );
        } else if (numericInputFormatted.length < 10) {
          // Optionally handle cases for numbers with fewer than 10 digits
          return numericInputFormatted; // or apply some other formatting
        }
        return input; // Return original input for other lengths
    }
  }

  static formatPhoneNumberWithCCAndExtension(
    input: string | null | undefined
  ): string {
    if (!input) return "";

    // Remove all non-digit characters
    const cleaned = input.replace(/\D/g, "");

    // Handle empty input
    if (cleaned.length === 0) return "";

    // Format as (XXX) XXX-XXXX for standard US numbers (10 digits)
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    }

    // For numbers with country code (1 + 10 digits)
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+1 ${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    // For numbers with extension (10 digits + ext)
    if (cleaned.length > 10) {
      const mainNumber = cleaned.slice(0, 10);
      const extension = cleaned.slice(10);
      return `${mainNumber.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")} ext. ${extension}`;
    }

    // Return as is if it doesn't match standard formats
    return input;
  }

  /**
   * Sanitize a string to create a valid URL slug.
   * Converts to lowercase, replaces spaces and special characters with hyphens,
   * removes consecutive hyphens, and trims leading/trailing hyphens.
   *
   * Example: "From a Dream to Action: Carrying on Dr. King's Call for Justice"
   *       -> "from-a-dream-to-action-carrying-on-dr-kings-call-for-justice"
   */
  static toUrlSlug(input: string | null | undefined): string {
    if (!input) return "";

    return (
      input
        .toLowerCase()
        // Replace apostrophes with nothing (King's -> Kings)
        .replace(/['']/g, "")
        // Replace spaces and special characters with hyphens
        .replace(/[\s:.,;!?&@#$%^*()+=\[\]{}|\\/<>~`"]+/g, "-")
        // Replace consecutive hyphens with single hyphen
        .replace(/-+/g, "-")
        // Remove leading/trailing hyphens
        .replace(/^-|-$/g, "")
    );
  }
}
